import { FC, useState } from "react";

import { serverGetSectionScoreReport } from "api/api-server-assessment";
import { CourseMenu } from "components/menus/CourseMenu";
import { TermMenu } from "components/menus/TermMenu";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ArrowDownIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { termSelectors } from "store/selectors";
import { toastErrorMessage } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { downloadCSVFile } from "utils/file";

export const StudentSectionScoresReport: FC = () => {
  const [selectedTermId, setSelectedTermId] = useState("");
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const terms = useAppSelector(termSelectors.selectAll);

  const handleDownloadCSV = async () => {
    if (!selectedTermId) {
      toastErrorMessage(t("assessment_summary_report.select_term_to_view_data"));
      return;
    }

    try {
      const params = selectedCourseId
        ? { term_id: selectedTermId, course_id: selectedCourseId }
        : { term_id: selectedTermId };

      const csvData = await serverGetSectionScoreReport(params);
      if (csvData.rows.length === 0) {
        toastErrorMessage(t("assessment_summary_report.no_data"));
        return;
      }

      // Construct the target filename for the download based on the selected Term.
      // TODO: the filename should also incorporate the selected Course's code, if any.
      const selectedTerm = terms.find((term) => term.id === selectedTermId);
      const termTitle = selectedTerm?.title || selectedTermId;
      const sanitizedTerm = termTitle.replace(/[^a-zA-Z0-9]/g, "");
      const filename = `ForumStudentCourseGrades-${sanitizedTerm}-${new Date().toISOString().split("T")[0]}.csv`;
      trackEvent("Home - Reports / Download section scores", {
        ...BUTTON_EVENT_CATEGORY,
        fileName: filename,
      });

      downloadCSVFile(csvData, filename);
    } catch {
      toastErrorMessage(t("assessment_summary_report.export_failed"));
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="h3">{t("student_section_scores_report.title")}</h2>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <TermMenu
            defaultToAll={false}
            direction="top"
            eventPrefix="Home - Reports / "
            menuType="menu"
            selectedTermId={selectedTermId}
            size="s"
            onTermChange={setSelectedTermId}
          />
        </div>

        <div className="flex items-center gap-2">
          <CourseMenu
            direction="top"
            menuType="menu"
            selectedCourseId={selectedCourseId}
            size="s"
            adminAccessibleOnly
            onCourseChange={setSelectedCourseId}
          />
        </div>

        <div className="flex items-center gap-2">
          <SquareButton
            disabled={!selectedTermId}
            eventName="Student Section Scores Report - Download CSV"
            kind="tertiary"
            size="s"
            onClick={handleDownloadCSV}
          >
            <IconText
              iconEnd={<ArrowDownIcon />}
              text={t("student_section_scores_report.download_csv")}
            />
          </SquareButton>
        </div>
      </div>
    </div>
  );
};
